// src/components/legal/TermsModal.tsx
// ─────────────────────────────────────────────────────────────
// TÉRMINOS Y CONDICIONES DE NIIZ Y ZANAHORIA TECH
//
// Se usa en dos modos:
//   · BLOQUEANTE (blocking): el gatekeeper lo monta cuando el usuario no ha
//     aceptado el contrato. NO se puede cerrar (sin X, sin ESC, sin clic en el
//     fondo) y exige marcar la casilla y pulsar "ACEPTAR Y ENTRAR A NIIZ".
//   · CONSULTA: se abre desde Configuración → Acerca de NIIZ para releerlos.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { FileText, ShieldCheck, Loader2, X } from 'lucide-react';
import {
  TERMS_ACCEPTANCE_LABEL,
  TERMS_CLOSING,
  TERMS_INTRO,
  TERMS_LAST_UPDATE,
  TERMS_PROVIDER,
  TERMS_SECTIONS,
  TERMS_TITLE,
  TERMS_VERSION,
} from '@/lib/termsContent';

interface TermsModalProps {
  isOpen: boolean;
  /// Modo bloqueante: no se puede cerrar y exige aceptación.
  blocking?: boolean;
  /// Fecha en la que el usuario aceptó (modo consulta).
  acceptedAt?: string | null;
  onClose?: () => void;
  onAccept?: () => void | Promise<void>;
}

export const TermsModal = ({ isOpen, blocking = false, acceptedAt, onClose, onAccept }: TermsModalProps) => {
  const [accepted, setAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // La casilla se reinicia cada vez que se abre el modal.
  useEffect(() => {
    if (isOpen) setAccepted(false);
  }, [isOpen]);

  // En modo bloqueante no hay cierre por teclado; en consulta sí con ESC.
  useEffect(() => {
    if (!isOpen || blocking) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, blocking, onClose]);

  if (!isOpen) return null;

  const handleAccept = async () => {
    if (!accepted || isSaving) return;
    setIsSaving(true);
    try {
      await onAccept?.();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={TERMS_TITLE}
      // En modo bloqueante el fondo NO cierra el modal.
      onClick={blocking ? undefined : () => onClose?.()}
    >
      <div
        className="w-full max-w-3xl max-h-[92vh] flex flex-col bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* CABECERA */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start gap-3 bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
          <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200">
              NiiZ · Zanahoria Tech
            </p>
            <h2 className="text-sm sm:text-base font-black leading-tight">{TERMS_TITLE}</h2>
            <p className="text-[11px] text-indigo-100 mt-0.5">
              Versión {TERMS_VERSION} · Actualizado {TERMS_LAST_UPDATE} · {TERMS_PROVIDER}
            </p>
          </div>
          {!blocking && (
            <button
              onClick={() => onClose?.()}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition shrink-0"
              title="Cerrar"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* AVISO DEL BLOQUEO */}
        {blocking && (
          <div className="px-5 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50">
            <p className="text-[11px] font-bold text-amber-800 dark:text-amber-300 leading-snug">
              🔒 La plataforma permanece bloqueada hasta que leas y aceptes los términos.
              Desplázate por el documento y marca la casilla de aceptación para entrar.
            </p>
          </div>
        )}

        {/* ARTICULADO CON SCROLL */}
        <div className="flex-1 overflow-y-auto px-5 py-4 text-[12px] leading-relaxed text-slate-700 dark:text-slate-300 space-y-4 bg-slate-50/60 dark:bg-slate-950/40">
          {TERMS_INTRO.map((paragraph, i) => (
            <p key={`intro-${i}`}>{paragraph}</p>
          ))}

          {TERMS_SECTIONS.map((section) => (
            <section key={section.id} className="space-y-2">
              <h3 className="font-black text-slate-900 dark:text-white text-[12px] uppercase tracking-wide flex items-start gap-2">
                <FileText size={13} className="mt-0.5 shrink-0 text-indigo-500" />
                {section.title}
              </h3>
              {section.body.map((paragraph, i) => (
                <p key={`${section.id}-${i}`} className="pl-5">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}

          {TERMS_CLOSING.map((paragraph, i) => (
            <p key={`closing-${i}`} className="font-semibold text-slate-800 dark:text-slate-200">
              {paragraph}
            </p>
          ))}

          <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-200 dark:border-slate-800">
            © {TERMS_LAST_UPDATE} Zanahoria Tech · NiiZ es un software desarrollado y operado por Zanahoria Tech.
            Documento versión {TERMS_VERSION}.
          </p>
        </div>

        {/* PIE: CASILLA OBLIGATORIA + BOTÓN DE ACEPTACIÓN */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-800 space-y-3">
          {blocking ? (
            <>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                />
                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 leading-snug">
                  {TERMS_ACCEPTANCE_LABEL}
                </span>
              </label>

              <button
                onClick={handleAccept}
                disabled={!accepted || isSaving}
                className="w-full py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition active:scale-[0.99]"
              >
                {isSaving ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                ACEPTAR Y ENTRAR A NIIZ
              </button>
              <p className="text-[10px] text-center text-slate-400">
                Al aceptar se registrará la fecha y la versión {TERMS_VERSION} del contrato en esta instalación.
              </p>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <p className="text-[10px] text-slate-500">
                {acceptedAt
                  ? `Aceptaste la versión ${TERMS_VERSION} el ${new Date(acceptedAt).toLocaleString('es-CO')}.`
                  : `Contrato vigente: versión ${TERMS_VERSION}.`}
              </p>
              <button
                onClick={() => onClose?.()}
                className="px-5 py-2.5 rounded-xl bg-slate-900 dark:bg-slate-700 text-white font-black text-[11px] uppercase tracking-widest"
              >
                Entendido
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
